class MineraliaHome extends HTMLElement {
  connectedCallback() {
    this.abortController = new AbortController();
    this.resizeObserver = new ResizeObserver(() => this.updateAllControls());

    this.querySelectorAll('[data-home-rail]').forEach((rail) => {
      this.resizeObserver.observe(rail);
      rail.addEventListener('scroll', () => this.updateControls(rail), {
        passive: true,
        signal: this.abortController.signal,
      });
    });

    this.querySelectorAll('[data-home-scroll]').forEach((button) => {
      button.addEventListener('click', () => this.scrollRail(button), {
        signal: this.abortController.signal,
      });
    });

    this.querySelectorAll('[data-home-tabs]').forEach((tablist) => this.setupTabs(tablist));

    // Theme editor: selecting a "Pestaña de productos" block opens that tab.
    document.addEventListener(
      'shopify:block:select',
      (event) => {
        const tab = event.target instanceof Element ? event.target.closest('[role="tab"]') : null;
        if (!tab || !this.contains(tab)) return;
        const tabs = [...tab.parentElement.querySelectorAll('[role="tab"]')];
        this.selectTab(tabs, tab);
      },
      { signal: this.abortController.signal }
    );

    // Al pasar de móvil a escritorio, las pestañas cargadas en móvil recuperan sus piezas.
    this.wideQuery.addEventListener('change', () => this.restoreDeferredCards(), {
      signal: this.abortController.signal,
    });

    this.setupHeroSlideshow();
    this.featuredLoad = this.loadFeatured();
    // Las flechas se ajustan en el siguiente fotograma (y el ResizeObserver al medir cada carril):
    // leer scrollWidth aquí obligaba a calcular el diseño en plena carga.
    requestAnimationFrame(() => this.updateAllControls());
  }

  /**
   * Destacados sin colección elegida: trae los más vendidos de toda la tienda
   * (sections/home-product-tab.liquid), omite los que ya muestra una pestaña renderizada
   * y deja el máximo configurado. Si no queda ninguno o falla la carga, la fila desaparece.
   * @returns {Promise<void>}
   */
  loadFeatured() {
    const rail = this.querySelector('[data-home-featured-src]');
    if (!(rail instanceof HTMLElement)) return Promise.resolve();
    const section = rail.closest('.mineralia-home__featured');

    return fetch(rail.dataset.homeFeaturedSrc, { signal: this.abortController.signal })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.text();
      })
      .then((html) => {
        const grid = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-home-tab-products]');
        if (!grid) throw new Error('Empty featured response');

        const shownIds = new Set(
          [...this.querySelectorAll('[role="tabpanel"] .mineralia-home__product-card')].map(
            (card) => card.dataset.productId
          )
        );
        const limit = Number(rail.dataset.homeFeaturedLimit) || 15;
        const cards = [...grid.children].filter((card) => !shownIds.has(card.dataset.productId)).slice(0, limit);
        if (!cards.length) throw new Error('No featured products left');

        rail.replaceChildren(...cards.map((card) => document.importNode(card, true)));
        rail.removeAttribute('aria-busy');
        requestAnimationFrame(() => this.updateControls(rail));
      })
      .catch((error) => {
        if (error?.name === 'AbortError') return;
        console.warn('[mineralia-home] Could not load featured products:', error);
        section?.remove();
      });
  }

  /**
   * Hero banners: never rotate on their own for visitors who prefer reduced motion, and do not
   * announce every automatic change to screen readers (they can still move through the slides).
   */
  setupHeroSlideshow() {
    const slideshow = this.querySelector('.mineralia-home__slideshow[autoplay]');
    if (!slideshow) return;
    slideshow.setAttribute('aria-live', 'off');

    if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    customElements.whenDefined('slideshow-component').then(() => {
      requestAnimationFrame(() => slideshow.pause?.());
    });
  }

  setupTabs(tablist) {
    const tabs = [...tablist.querySelectorAll('[role="tab"]')];
    const { signal } = this.abortController;

    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => this.selectTab(tabs, tab), { signal });
      // Start loading a deferred tab as soon as the visitor shows intent.
      const prefetch = () => this.loadPanel(this.panelFor(tab));
      tab.addEventListener('pointerenter', prefetch, { signal });
      tab.addEventListener('focus', prefetch, { signal });
      tab.addEventListener(
        'keydown',
        (event) => {
          const keys = {
            ArrowRight: index + 1,
            ArrowLeft: index - 1,
            Home: 0,
            End: tabs.length - 1,
          };
          if (!(event.key in keys)) return;

          event.preventDefault();
          const next = tabs[(keys[event.key] + tabs.length) % tabs.length];
          this.selectTab(tabs, next);
          next.focus();
        },
        { signal }
      );
    });
  }

  selectTab(tabs, selected) {
    tabs.forEach((tab) => {
      const isSelected = tab === selected;
      tab.setAttribute('aria-selected', String(isSelected));
      tab.tabIndex = isSelected ? 0 : -1;

      const panel = this.panelFor(tab);
      if (panel) panel.hidden = !isSelected;
      if (panel && isSelected) this.loadPanel(panel);
    });

    const tablist = selected.parentElement;
    if (tablist && tablist.scrollWidth > tablist.clientWidth) {
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      tablist.scrollTo({
        left: selected.offsetLeft - tablist.offsetLeft - 14,
        behavior: reduceMotion ? 'auto' : 'smooth',
      });
    }
  }

  /** @param {Element} tab */
  panelFor(tab) {
    const id = tab.getAttribute('aria-controls');
    return id ? this.querySelector(`#${CSS.escape(id)}`) : null;
  }

  /** @type {WeakMap<Element, Promise<void>>} */
  #panelLoads = new WeakMap();

  /** Piezas que una pestaña cargada en móvil no insertó, por si la pantalla se ensancha. */
  #deferredCards = new Map();

  wideQuery = window.matchMedia('(min-width: 750px)');

  restoreDeferredCards() {
    if (!this.wideQuery.matches) return;
    this.#deferredCards.forEach((cards, grid) => grid.append(...cards));
    this.#deferredCards.clear();
    this.updateAllControls();
  }

  /**
   * Loads a tab rendered without products (sections/home-product-tab.liquid). Each panel is
   * fetched once; panels are independent, so responses arriving out of order are harmless.
   * @param {Element | null} panel
   */
  loadPanel(panel) {
    if (!(panel instanceof HTMLElement) || !panel.dataset.homeTabSrc) return;
    if (panel.dataset.homeTabLoaded === 'true' || this.#panelLoads.has(panel)) return;

    const status = panel.querySelector('[data-home-tab-status]');
    panel.setAttribute('aria-busy', 'true');
    if (status) status.textContent = '';

    const load = fetch(panel.dataset.homeTabSrc, { signal: this.abortController.signal })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.text();
      })
      // Espera a Destacados para conocer sus piezas antes de excluirlas.
      .then((html) => (this.featuredLoad ?? Promise.resolve()).then(() => html))
      .then((html) => {
        const grid = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-home-tab-products]');
        if (!grid) throw new Error('Empty tab response');

        // Las mismas piezas no se repiten entre Destacados y las pestañas diferidas: primero se
        // excluyen y después se aplica el límite, para rellenar con las siguientes de la colección.
        const featuredIds = new Set(
          [...this.querySelectorAll('.mineralia-home__featured .mineralia-home__product-card')].map(
            (card) => card.dataset.productId
          )
        );
        [...grid.children].forEach((card) => {
          if (featuredIds.has(card.dataset.productId)) card.remove();
        });
        const limit = Number(panel.dataset.homeTabLimit) || grid.children.length;
        [...grid.children].slice(limit).forEach((card) => card.remove());
        grid.removeAttribute('data-home-tab-products');

        // En móvil sólo se insertan las piezas visibles («Productos por pestaña en móvil»).
        const mobileLimit = Number(panel.dataset.homeTabMobileLimit) || limit;
        const imported = document.importNode(grid, true);
        if (!this.wideQuery.matches && imported.children.length > mobileLimit) {
          const rest = [...imported.children].slice(mobileLimit);
          rest.forEach((card) => card.remove());
          this.#deferredCards.set(imported, rest);
        }

        panel.querySelector('[data-home-tab-placeholder]')?.replaceWith(imported);
        if (grid.children.length) {
          status?.remove();
        } else if (status) {
          status.textContent = 'Estas piezas ya están en Destacados. Puedes ver la colección completa abajo.';
        }
        panel.dataset.homeTabLoaded = 'true';
      })
      .catch((error) => {
        if (error?.name === 'AbortError') return;
        console.warn('[mineralia-home] Could not load tab products:', error);
        this.#panelLoads.delete(panel);
        if (!status) return;
        status.textContent = 'No pudimos cargar estas piezas.';
        const retry = document.createElement('button');
        retry.type = 'button';
        retry.textContent = 'Reintentar';
        retry.addEventListener('click', () => this.loadPanel(panel), { once: true });
        status.append(retry);
      })
      .finally(() => {
        panel.removeAttribute('aria-busy');
      });

    this.#panelLoads.set(panel, load);
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.resizeObserver?.disconnect();
  }

  scrollRail(button) {
    const rail = this.querySelector(`#${CSS.escape(button.dataset.homeTarget)}`);
    if (!rail) return;

    const direction = button.dataset.homeScroll === 'next' ? 1 : -1;
    const distance = Math.max(rail.clientWidth * 0.82, 260);
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    rail.scrollBy({
      left: distance * direction,
      behavior: reduceMotion ? 'auto' : 'smooth',
    });
  }

  updateAllControls() {
    this.querySelectorAll('[data-home-rail]').forEach((rail) => this.updateControls(rail));
  }

  updateControls(rail) {
    const startButton = this.querySelector(
      `[data-home-target="${CSS.escape(rail.id)}"][data-home-scroll="previous"]`
    );
    const endButton = this.querySelector(
      `[data-home-target="${CSS.escape(rail.id)}"][data-home-scroll="next"]`
    );
    const maxScroll = Math.max(rail.scrollWidth - rail.clientWidth, 0);

    const controls = startButton?.closest('[data-home-rail-controls]');
    if (controls) controls.hidden = maxScroll <= 2;
    if (startButton) startButton.disabled = rail.scrollLeft <= 2;
    if (endButton) endButton.disabled = rail.scrollLeft >= maxScroll - 2 || maxScroll === 0;
  }
}

if (!customElements.get('mineralia-home')) {
  customElements.define('mineralia-home', MineraliaHome);
}
